<template>
  <!-- calendar-open: Parent ticket cards use this marker to stay above neighboring content until the calendar closes. -->
  <div ref="root" :class="['w-full', { 'calendar-open': calendarVisible }]">
    <div class="relative w-full">
      <InputLabel
        :placeholder="placeholder"
        :value="value?.toString()"
        :class="errorMessage ? 'text-error' : 'text-text'"
      />
      <button
        type="button"
        :class="[
          'px-4 w-full border font-normal rounded-sm bg-surface-light h-14 cursor-pointer text-headings focus:outline-none focus:ring-1 focus:ring-input-border transition-colors 4 text-left',
          value ? 'pt-6 pb-2' : 'py-4',
          errorMessage ? 'border-error text-error' : 'border-input-border',
        ]"
        @click="setOpen(!open)"
        :disabled="disabled"
        :readonly="readonly"
      >
        <span class="capitalize" v-if="value">{{
          formatDate(value, { locale: $i18n.locale })
        }}</span>

        <CalendarIcon
          class="absolute right-4 top-1/2 transform -translate-y-1/2 text-text"
        />
      </button>
      <BaseSlideTransition @after-leave="onCalendarAfterLeave">
        <div
          v-show="open"
          class="absolute z-10 mt-1 max-w-full bg-white border border-input-border rounded-sm shadow-lg"
        >
          <div class="p-2 md:p-4">
            <BaseDatePicker
              :min-date="minDate"
              :max-date="maxDate"
              @close="setOpen(false)"
              @update:modelValue="onDateChange"
            />
          </div>
        </div>
      </BaseSlideTransition>
    </div>
    <div v-if="errorMessage" class="text-sm block pt-2 text-error">
      {{ errorMessage }}
    </div>
  </div>
</template>

<script setup lang="ts">
  import { type RuleExpression,useField } from 'vee-validate';
  import { onBeforeUnmount,onMounted, ref } from 'vue';

  import CalendarIcon from '@/components/icons/CalendarIcon.vue';
  import { formatDate } from '@/utils/formatDate';

  import BaseDatePicker from '../BaseDatePicker.vue';
  import BaseSlideTransition from '../BaseSlideTransition.vue';
  import InputLabel from './InputLabel.vue';

  interface Props {
    name: string;
    modelValue?: string;
    placeholder?: string;
    disabled?: boolean;
    readonly?: boolean;
    defaultValue?: string;
    minDate?: Date;
    maxDate?: Date;
    required?: boolean;
    rules?: RuleExpression<unknown>;
    closeOnSelect?: boolean;
  }

  const props = withDefaults(defineProps<Props>(), {});
  const emit = defineEmits<{ 'update:modelValue': [value: string] }>();

  const open = ref(false);
  const calendarVisible = ref(false);
  const root = ref<HTMLElement | null>(null);

  const { value, errorMessage, handleChange } = useField<number>(
    () => props.name,
    props.rules as RuleExpression<number>,
    { syncVModel: true }
  );

  function setOpen(isOpen: boolean) {
    open.value = isOpen;
    if (isOpen) {
      calendarVisible.value = true;
    }
  }

  function onCalendarAfterLeave() {
    if (!open.value) {
      calendarVisible.value = false;
    }
  }

  function onDateChange(val: number | null) {
    handleChange(val);
    emit('update:modelValue', val?.toString() || '');
    if (props.closeOnSelect) {
      setOpen(false);
    }
  }

  function handleClickOutside(event: MouseEvent) {
    if (
      open.value &&
      root.value &&
      !root.value.contains(event.target as Node)
    ) {
      setOpen(false);
    }
  }

  onMounted(() => {
    document.addEventListener('mousedown', handleClickOutside);
  });
  onBeforeUnmount(() => {
    document.removeEventListener('mousedown', handleClickOutside);
  });
</script>
